/**
 * PupClub Couture - Wishlist Feature
 * For Shopify Dawn v10
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'pupclub_wishlist';
  const SELECTIONS_KEY = 'pupclub_wishlist_selections';
  const ANIMATION_DURATION = 300;

  /* ─── State ─── */
  let wishlistItems = [];
  let cartItems = [];
  let drawerOpen = false;
  let variantSelections = {}; // { productId: variantId }

  /* ─── Helpers ─── */
  function getWishlist() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Wishlist: Failed to save', e);
    }
    wishlistItems = items;
    updateAllUI();
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  function isInWishlist(productId) {
    return wishlistItems.some((item) => item.productId === productId);
  }

  function loadVariantSelections() {
    try {
      const data = localStorage.getItem(SELECTIONS_KEY);
      variantSelections = data ? JSON.parse(data) : {};
    } catch (e) {
      variantSelections = {};
    }
  }

  function saveVariantSelections() {
    try {
      localStorage.setItem(SELECTIONS_KEY, JSON.stringify(variantSelections));
    } catch (e) {
      console.error('Wishlist: Failed to save variant selections', e);
    }
  }

  function addToWishlist(product) {
    if (isInWishlist(product.productId)) return;
    wishlistItems.push(product);
    saveWishlist(wishlistItems);
  }

  function removeFromWishlist(productId) {
    wishlistItems = wishlistItems.filter((item) => item.productId !== productId);
    delete variantSelections[String(productId)];
    saveVariantSelections();
    saveWishlist(wishlistItems);
  }

  function isVariantInCart(variantId) {
    return cartItems.some((item) => item.variant_id === variantId);
  }

  /* ─── Cart API ─── */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      cartItems = cart.items || [];
      return cart;
    } catch (e) {
      console.error('Wishlist: Failed to fetch cart', e);
      return { items: [] };
    }
  }

  /**
   * Refresh the cart drawer by triggering a no-op cart update with sections.
   * This uses /cart/update.js with an empty updates object, which doesn't change
   * the cart but returns fresh section HTML. We then use Dawn's native
   * renderContents() to update the drawer properly.
   * 
   * The key: renderContents is called WITHOUT opening the drawer by temporarily
   * overriding the open() method.
   */
  /**
   * Update the cart icon badge in the header using fresh section HTML.
   * Targets #cart-icon-bubble directly since it's not wrapped in a shopify-section div.
   */
  async function updateCartIconBubble() {
    try {
      const res = await fetch('/?sections=cart-icon-bubble');
      if (!res.ok) return;
      const sections = await res.json();
      if (sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const newDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = newDoc.querySelector('#cart-icon-bubble');
        const existingBubble = document.querySelector('#cart-icon-bubble');
        if (newBubble && existingBubble) {
          existingBubble.innerHTML = newBubble.innerHTML;
        }
      }
    } catch (e) {
      console.error('Wishlist: Failed to update cart icon bubble', e);
    }
  }

  async function refreshCartDrawerSilently() {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {},
          sections: ['cart-drawer', 'cart-icon-bubble'],
        }),
      });
      if (!res.ok) return;
      const cartData = await res.json();

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Temporarily prevent the drawer from opening
        const originalOpen = cartDrawer.open;
        cartDrawer.open = function () {}; // no-op

        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(cartData);

        // Restore the open method after a tick
        setTimeout(() => {
          cartDrawer.open = originalOpen;
        }, 100);
      }

      // Also update the cart icon badge directly
      await updateCartIconBubble();
    } catch (e) {
      console.error('Wishlist: Failed to refresh cart drawer', e);
    }
  }

  async function addToCart(variantId, quantity = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      const data = await res.json();
      await fetchCart();
      await refreshCartDrawerSilently();
      return data;
    } catch (e) {
      console.error('Wishlist: Failed to add to cart', e);
      return null;
    }
  }

  async function removeFromCart(variantId) {
    try {
      // Remove the item from cart
      const updates = {};
      updates[variantId.toString()] = 0;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      await fetchCart();

      // Nuclear refresh: fetch fresh section HTML and replace the entire
      // cart-drawer element. renderContents doesn't work reliably on remove
      // because the BundleUpgrade widget corrupts the drawer state.
      const sectionRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      if (sectionRes.ok) {
        const sections = await sectionRes.json();

        if (sections['cart-drawer']) {
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newCartDrawer = newDoc.querySelector('cart-drawer');
          const existingCartDrawer = document.querySelector('cart-drawer');

          if (newCartDrawer && existingCartDrawer) {
            // Replace the entire cart-drawer element with the fresh one
            existingCartDrawer.replaceWith(newCartDrawer);
          }
        }

        // Update cart icon bubble directly from our cart data
        updateCartBadge();
        // Also try the section-based update as backup
        updateCartIconBubble();
      }
      
      // Insurance: update badge again after widget settles
      setTimeout(() => updateCartBadge(), 1500);
    } catch (e) {
      console.error('Wishlist: Failed to remove from cart', e);
    }
  }

  async function addAllToCart() {
    const drawerItems = document.querySelectorAll('.wishlist-drawer__item');
    const itemsToAdd = [];

    drawerItems.forEach((el) => {
      const variantId = el.dataset.selectedVariantId;
      if (variantId && !isVariantInCart(parseInt(variantId))) {
        itemsToAdd.push({ id: parseInt(variantId), quantity: 1 });
      }
    });

    if (itemsToAdd.length === 0) return;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd }),
      });
      if (!res.ok) throw new Error('Add all to cart failed');
      await fetchCart();
      await refreshCartDrawerSilently();
      const selections = captureDrawerSelections();
      renderDrawerItems(selections);
      showNotification('All items added to basket', null, null);
    } catch (e) {
      console.error('Wishlist: Failed to add all to cart', e);
    }
  }

  /* ─── UI Updates ─── */
  function updateAllUI() {
    updateWishlistBadge();
    updateProductPageButtons();
    if (drawerOpen) {
      const selections = captureDrawerSelections();
      renderDrawerItems(selections);
    }
  }

  function updateWishlistBadge() {
    const badge = document.querySelector('.wishlist-header__count');
    if (!badge) return;
    const count = wishlistItems.length;
    badge.textContent = count;
    badge.style.display = count > 0 ? 'flex' : 'none';

    // Update drawer title count
    const drawerCount = document.querySelector('.wishlist-drawer__count');
    if (drawerCount) {
      drawerCount.textContent = count > 0 ? `(${count} ${count === 1 ? 'Product' : 'Products'})` : '';
    }
  }

  function updateCartBadge() {
    // Update Dawn's native cart badge
    const cartBadges = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
    const totalQty = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    cartBadges.forEach((badge) => {
      badge.textContent = totalQty;
    });
    const bubbles = document.querySelectorAll('.cart-count-bubble');
    bubbles.forEach((bubble) => {
      bubble.style.display = totalQty > 0 ? '' : 'none';
    });
  }

  function updateProductPageButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const productId = parseInt(btn.dataset.productId);
      const inWishlist = isInWishlist(productId);
      btn.classList.toggle('wishlist-btn--active', inWishlist);
      btn.setAttribute('aria-label', inWishlist ? 'Remove from wishlist' : 'Add to wishlist');
      const heartFill = btn.querySelector('.wishlist-heart-fill');
      const heartOutline = btn.querySelector('.wishlist-heart-outline');
      if (heartFill) heartFill.style.display = inWishlist ? 'block' : 'none';
      if (heartOutline) heartOutline.style.display = inWishlist ? 'none' : 'block';
    });
  }

  /* ─── Drawer ─── */
  function openDrawer() {
    const drawer = document.querySelector('.wishlist-drawer');
    const overlay = document.querySelector('.wishlist-overlay');
    if (!drawer || !overlay) return;

    // Build selections Map from persisted variantSelections
    const selections = new Map();
    for (const [pid, vid] of Object.entries(variantSelections)) {
      selections.set(pid, vid);
    }
    renderDrawerItems(selections);
    drawer.classList.add('wishlist-drawer--open');
    overlay.classList.add('wishlist-overlay--visible');
    document.body.style.overflow = 'hidden';
    drawerOpen = true;

    // Focus trap
    setTimeout(() => {
      const closeBtn = drawer.querySelector('.wishlist-drawer__close');
      if (closeBtn) closeBtn.focus();
    }, ANIMATION_DURATION);
  }

  function closeDrawer() {
    const drawer = document.querySelector('.wishlist-drawer');
    const overlay = document.querySelector('.wishlist-overlay');
    if (!drawer || !overlay) return;

    drawer.classList.remove('wishlist-drawer--open');
    overlay.classList.remove('wishlist-overlay--visible');
    document.body.style.overflow = '';
    drawerOpen = false;
  }

  /**
   * Capture the currently selected variant IDs from the drawer items.
   * Returns a Map of productId -> selectedVariantId
   */
  function captureDrawerSelections() {
    document.querySelectorAll('.wishlist-drawer__item').forEach((el) => {
      const productId = el.dataset.productId;
      const variantId = el.dataset.selectedVariantId;
      if (productId && variantId) {
        variantSelections[productId] = variantId;
      }
    });
    saveVariantSelections();
    // Return as Map for renderDrawerItems compatibility
    const selections = new Map();
    for (const [pid, vid] of Object.entries(variantSelections)) {
      selections.set(pid, vid);
    }
    return selections;
  }

  function renderDrawerItems(savedSelections = null) {
    const container = document.querySelector('.wishlist-drawer__items');
    const emptyState = document.querySelector('.wishlist-drawer__empty');
    const footer = document.querySelector('.wishlist-drawer__footer');
    const addAllBtn = document.querySelector('.wishlist-drawer__add-all');

    if (!container) return;

    if (wishlistItems.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = 'flex';
      if (footer) footer.style.display = 'none';
      if (addAllBtn) addAllBtn.style.display = 'none';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (footer) footer.style.display = 'flex';
    if (addAllBtn) addAllBtn.style.display = 'block';

    container.innerHTML = wishlistItems
      .map((item) => {
        // Use saved selection if available, otherwise default to first variant
        const savedVariantId = savedSelections ? savedSelections.get(String(item.productId)) : null;
        let selectedVariant = item.variants ? item.variants[0] : null;
        if (savedVariantId && item.variants) {
          const found = item.variants.find((v) => String(v.id) === savedVariantId);
          if (found) selectedVariant = found;
        }
        const selectedVariantId = selectedVariant ? selectedVariant.id : '';
        const inCart = selectedVariantId ? isVariantInCart(selectedVariantId) : false;

        return `
        <div class="wishlist-drawer__item" 
             data-product-id="${item.productId}" 
             data-selected-variant-id="${selectedVariantId}"
             data-product-handle="${item.handle || ''}">
          <div class="wishlist-drawer__item-image">
            <a href="${item.url || '#'}">
              <img src="${item.image || ''}" alt="${item.title || ''}" loading="lazy" />
            </a>
          </div>
          <div class="wishlist-drawer__item-details">
            <a href="${item.url || '#'}" class="wishlist-drawer__item-title">${item.title || 'Product'}</a>
            <div class="wishlist-drawer__item-price" data-base-price="${selectedVariant ? selectedVariant.price : item.price || 0}">
              ${renderPrice(selectedVariant || item, item.tags)}
            </div>
            ${renderVariantSelectors(item, selectedVariant)}
            <div class="wishlist-drawer__item-actions">
              <button class="wishlist-drawer__cart-btn ${inCart ? 'wishlist-drawer__cart-btn--in-cart' : ''}" 
                      data-variant-id="${selectedVariantId}"
                      ${!selectedVariantId ? 'disabled' : ''}>
                ${inCart ? 'Remove from basket' : 'Add to basket'}
              </button>
              ${inCart ? '<span class="wishlist-drawer__in-cart-tag">In basket</span>' : ''}
            </div>
            <button class="wishlist-drawer__remove" data-product-id="${item.productId}" aria-label="Remove from wishlist">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
        </div>
      `;
      })
      .join('');

    // Update footer cart button
    updateDrawerFooter();

    // Bind events
    bindDrawerItemEvents();
  }

  function renderVariantSelectors(item, selectedVariant) {
    if (!item.variants || item.variants.length <= 1) return '';
    if (!item.options || item.options.length === 0) return '';

    // Determine which option values are currently selected
    const selectedOptions = selectedVariant ? selectedVariant.options : (item.variants[0] ? item.variants[0].options : []);

    let html = '<div class="wishlist-drawer__variants">';

    item.options.forEach((option, optionIndex) => {
      const values = [...new Set(item.variants.map((v) => v.options[optionIndex]).filter(Boolean))];
      if (values.length <= 1) return;

      const isColor = /colou?r/i.test(option);
      const selectedValue = selectedOptions[optionIndex] || '';

      html += `<div class="wishlist-drawer__option" data-option-index="${optionIndex}">
        <span class="wishlist-drawer__option-label">${option}</span>
        <div class="wishlist-drawer__option-values ${isColor ? 'wishlist-drawer__option-values--color' : ''}">`;

      values.forEach((value) => {
        const isSelected = value === selectedValue;
        if (isColor) {
          const colorMap = getColorHex(value);
          html += `<button class="wishlist-drawer__color-swatch ${isSelected ? 'wishlist-drawer__swatch--selected' : ''}" 
                    data-option-index="${optionIndex}" 
                    data-value="${value}" 
                    title="${value}"
                    aria-label="${value}"
                    style="background-color: ${colorMap}">
                  </button>`;
        } else {
          html += `<button class="wishlist-drawer__size-btn ${isSelected ? 'wishlist-drawer__size-btn--selected' : ''}" 
                    data-option-index="${optionIndex}" 
                    data-value="${value}">
                    ${abbreviateSize(value)}
                  </button>`;
        }
      });

      html += `</div></div>`;
    });

    html += '</div>';
    return html;
  }

  function abbreviateSize(value) {
    const abbrevs = {
      'extra small': 'XS',
      'small': 'S',
      'medium': 'M',
      'large': 'L',
      'extra large': 'XL',
      'extra extra large': 'XXL',
      'xx-large': 'XXL',
      'x-small': 'XS',
      'x-large': 'XL',
    };
    const lower = value.toLowerCase().trim();
    return abbrevs[lower] || value;
  }

  function getColorHex(colorName) {
    const colors = {
      black: '#000000', white: '#ffffff', red: '#c0392b', blue: '#2980b9',
      green: '#27ae60', yellow: '#f1c40f', pink: '#e91e8e', purple: '#8e44ad',
      orange: '#e67e22', brown: '#8b5e3c', grey: '#95a5a6', gray: '#95a5a6', grey: '#95a5a6',
      navy: '#1a2744', beige: '#f5f0e1', cream: '#fffdd0', gold: '#d4a843',
      silver: '#c0c0c0', tan: '#d2b48c', coral: '#ff6f61', teal: '#008080',
      burgundy: '#800020', olive: '#808000', sage: '#b2ac88', rust: '#b7410e',
      plum: '#8e4585', mint: '#98fb98', lilac: '#c8a2c8', sky: '#87ceeb',
      charcoal: '#36454f', stone: '#928e85', natural: '#f5e6ca',
      'mint green': '#98FF98',
      'pastel pink': '#F8C8DC',
      'khaki': '#5D6532',
      'mauve': '#FF599E',
      
    };
    const key = colorName.toLowerCase().trim();
    return colors[key] || '#cccccc';
  }

  function findVariantByOptions(item, selectedOptions) {
    if (!item.variants) return null;
    return item.variants.find((variant) => {
      return selectedOptions.every((val, idx) => {
        return variant.options[idx] === val;
      });
    });
  }

  function bindDrawerItemEvents() {
    // Remove from wishlist
    document.querySelectorAll('.wishlist-drawer__remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = parseInt(btn.dataset.productId);
        const itemEl = btn.closest('.wishlist-drawer__item');
        itemEl.style.animation = 'wishlistSlideOut 0.25s ease forwards';
        setTimeout(() => {
          removeFromWishlist(productId);
        }, 250);
      });
    });

    // Cart add/remove buttons
    document.querySelectorAll('.wishlist-drawer__cart-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = parseInt(btn.dataset.variantId);
        if (!variantId) return;

        btn.disabled = true;
        btn.classList.add('wishlist-drawer__cart-btn--loading');

        const inCart = isVariantInCart(variantId);

        if (inCart) {
          await removeFromCart(variantId);
        } else {
          const result = await addToCart(variantId);
          if (result) {
            const itemEl = btn.closest('.wishlist-drawer__item');
            const title = itemEl.querySelector('.wishlist-drawer__item-title')?.textContent || '';
            const img = itemEl.querySelector('img')?.src || '';
            showNotification(title, img, variantId);
          }
        }

        btn.disabled = false;
        btn.classList.remove('wishlist-drawer__cart-btn--loading');
        const selections = captureDrawerSelections();
        renderDrawerItems(selections);
      });
    });

    // Variant option buttons
    document.querySelectorAll('.wishlist-drawer__size-btn, .wishlist-drawer__color-swatch').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const itemEl = btn.closest('.wishlist-drawer__item');
        const productId = parseInt(itemEl.dataset.productId);
        const optionIndex = parseInt(btn.dataset.optionIndex);
        const value = btn.dataset.value;
        const item = wishlistItems.find((i) => i.productId === productId);
        if (!item) return;

        // Update selected state in this option group
        const optionGroup = btn.closest('.wishlist-drawer__option-values');
        optionGroup.querySelectorAll('button').forEach((b) => {
          b.classList.remove('wishlist-drawer__swatch--selected', 'wishlist-drawer__size-btn--selected');
        });
        if (btn.classList.contains('wishlist-drawer__color-swatch')) {
          btn.classList.add('wishlist-drawer__swatch--selected');
        } else {
          btn.classList.add('wishlist-drawer__size-btn--selected');
        }

        // Gather all selected options
        const selectedOptions = [];
        itemEl.querySelectorAll('.wishlist-drawer__option').forEach((optEl) => {
          const idx = parseInt(optEl.dataset.optionIndex);
          const selected = optEl.querySelector('.wishlist-drawer__swatch--selected, .wishlist-drawer__size-btn--selected');
          if (selected) {
            selectedOptions[idx] = selected.dataset.value;
          }
        });

        // Find matching variant
        const variant = findVariantByOptions(item, selectedOptions);
        if (variant) {
          itemEl.dataset.selectedVariantId = variant.id;

          // Persist the selection
          variantSelections[String(productId)] = String(variant.id);
          saveVariantSelections();

          // Update price
          const priceEl = itemEl.querySelector('.wishlist-drawer__item-price');
          if (priceEl) priceEl.innerHTML = renderPrice(variant, item.tags);

          // Update image if variant has one
          if (variant.featured_image) {
            const img = itemEl.querySelector('img');
            if (img) img.src = variant.featured_image;
          }

          // Update cart button state
          const cartBtn = itemEl.querySelector('.wishlist-drawer__cart-btn');
          const inCart = isVariantInCart(variant.id);
          cartBtn.dataset.variantId = variant.id;
          cartBtn.disabled = !variant.available;
          cartBtn.classList.toggle('wishlist-drawer__cart-btn--in-cart', inCart);
          cartBtn.textContent = !variant.available ? 'Sold out' : inCart ? 'Remove from basket' : 'Add to basket';

          // Update in-cart tag
          const existingTag = itemEl.querySelector('.wishlist-drawer__in-cart-tag');
          if (inCart && !existingTag) {
            const actionsDiv = itemEl.querySelector('.wishlist-drawer__item-actions');
            actionsDiv.insertAdjacentHTML('beforeend', '<span class="wishlist-drawer__in-cart-tag">In basket</span>');
          } else if (!inCart && existingTag) {
            existingTag.remove();
          }
        } else {
          // No matching variant - disable add to cart
          const cartBtn = itemEl.querySelector('.wishlist-drawer__cart-btn');
          cartBtn.disabled = true;
          cartBtn.textContent = 'Unavailable';
          itemEl.dataset.selectedVariantId = '';
        }
      });
    });
  }

  function updateDrawerFooter() {
    const footerBtn = document.querySelector('.wishlist-drawer__view-cart');
    if (!footerBtn) return;
    const totalQty = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    footerBtn.textContent = `View basket (${totalQty})`;
    footerBtn.style.display = totalQty > 0 ? 'flex' : 'none';
  }

  /* ─── Notification Toast ─── */
  /**
   * Position a toast dropdown relative to a header icon.
   * If the icon is visible in the viewport, position below it with an arrow.
   * If the header is hidden (scrolled away), position at top-right of viewport.
   */
  function positionToast(toast, iconSelector) {
    const icon = document.querySelector(iconSelector);
    
    if (icon) {
      const rect = icon.getBoundingClientRect();
      const iconVisible = rect.top >= -10 && rect.top < window.innerHeight;
      
      if (iconVisible) {
        // Position below the icon
        const iconCenterX = rect.left + rect.width / 2;
        const toastWidth = Math.min(400, window.innerWidth - 24);
        
        // Calculate left position so toast is roughly centred on the icon
        // but doesn't overflow off-screen
        let left = iconCenterX - toastWidth / 2;
        const rightEdge = left + toastWidth;
        if (rightEdge > window.innerWidth - 12) {
          left = window.innerWidth - 12 - toastWidth;
        }
        if (left < 12) left = 12;
        
        // Arrow position relative to toast
        const arrowLeft = Math.max(16, Math.min(iconCenterX - left, toastWidth - 16));
        
        toast.style.position = 'fixed';
        toast.style.top = (rect.bottom + 10) + 'px';
        toast.style.left = left + 'px';
        toast.style.right = 'auto';
        toast.style.bottom = 'auto';
        toast.classList.add('wishlist-toast--dropdown');
        toast.style.setProperty('--arrow-left', arrowLeft + 'px');
        return;
      }
    }
    
    // Fallback: top-right fixed position
    toast.style.position = 'fixed';
    toast.style.top = '16px';
    toast.style.right = '16px';
    toast.style.left = 'auto';
    toast.style.bottom = 'auto';
    toast.classList.add('wishlist-toast--top');
  }

  function showNotification(title, image, variantId) {
    const existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();

    const isGeneric = !image;

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.innerHTML = `
      <div class="wishlist-toast__inner">
        ${!isGeneric ? `<img class="wishlist-toast__image" src="${image}" alt="" />` : ''}
        <div class="wishlist-toast__content">
          <svg class="wishlist-toast__tick" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="8" fill="#27ae60"/>
            <path d="M5 8l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="wishlist-toast__text">${isGeneric ? title : `${title} added to basket`}</span>
        </div>
        <button class="wishlist-toast__cart-btn" aria-label="View basket">View basket</button>
      </div>
    `;

    document.body.appendChild(toast);
    positionToast(toast, '#cart-icon-bubble');

    requestAnimationFrame(() => {
      toast.classList.add('wishlist-toast--visible');
    });

    toast.querySelector('.wishlist-toast__cart-btn').addEventListener('click', () => {
      toast.remove();
      closeDrawer();
      setTimeout(() => {
        openCartDrawer();
      }, ANIMATION_DURATION);
    });

    setTimeout(() => {
      toast.classList.remove('wishlist-toast--visible');
      setTimeout(() => toast.remove(), ANIMATION_DURATION);
    }, 4500);
  }

  function showWishlistNotification(title, image) {
    const existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.innerHTML = `
      <div class="wishlist-toast__inner">
        ${image ? `<img class="wishlist-toast__image" src="${image}" alt="" />` : ''}
        <div class="wishlist-toast__content">
          <svg class="wishlist-toast__tick" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="8" fill="var(--wishlist-accent, #e74c6f)"/>
            <path d="M5 8l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="wishlist-toast__text">${title} added to wishlist</span>
        </div>
        <button class="wishlist-toast__wishlist-btn" aria-label="View wishlist">View wishlist</button>
      </div>
    `;

    document.body.appendChild(toast);
    positionToast(toast, '.wishlist-header__btn');

    requestAnimationFrame(() => {
      toast.classList.add('wishlist-toast--visible');
    });

    toast.querySelector('.wishlist-toast__wishlist-btn').addEventListener('click', () => {
      toast.remove();
      openDrawer();
    });

    setTimeout(() => {
      toast.classList.remove('wishlist-toast--visible');
      setTimeout(() => toast.remove(), ANIMATION_DURATION);
    }, 4500);
  }

  function openCartDrawer() {
    // Dawn's cart drawer - try common selectors
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const openBtn = document.querySelector('[data-cart-drawer-toggle]') ||
                       document.querySelector('.header__icon--cart') ||
                       document.querySelector('#cart-icon-bubble');
      if (openBtn) {
        openBtn.click();
        return;
      }
      // Fallback: try to open via Dawn's cart drawer API
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
    }
    // Last resort: navigate to cart
    window.location.href = '/cart';
  }

  /* ─── Money Formatting ─── */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents);
    const amount = (cents / 100).toFixed(2);
    // Use Shopify's currency if available
    const currency = window.Shopify?.currency?.active || 'GBP';
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
    return `${symbol}${amount}`;
  }

  function renderPrice(variantOrItem, tags) {
    let price = variantOrItem.price || 0;
    let compareAt = variantOrItem.compare_at_price || 0;

    // Force 25% discount for harness-bundle tagged products
    if (tags && tags.some(t => t.toLowerCase() === 'harness-bundle')) {
      if (!compareAt || compareAt <= price) {
        compareAt = price;
      }
      price = Math.round(price * 0.75);
    }

    if (compareAt && compareAt > price) {
      return `<span class="wishlist-drawer__price-compare">${formatMoney(compareAt)}</span> <span class="wishlist-drawer__price-sale">${formatMoney(price)}</span>`;
    }
    return formatMoney(price);
  }

  /* ─── Product Page Integration ─── */
  function initProductPageButton() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = parseInt(btn.dataset.productId);

        if (isInWishlist(productId)) {
          removeFromWishlist(productId);
          btn.classList.add('wishlist-btn--bounce-out');
          setTimeout(() => btn.classList.remove('wishlist-btn--bounce-out'), 300);
        } else {
          const productData = gatherProductData(btn);
          addToWishlist(productData);
          showWishlistNotification(productData.title, productData.image);
          btn.classList.add('wishlist-btn--bounce');
          setTimeout(() => btn.classList.remove('wishlist-btn--bounce'), 600);
        }
      });
    });
  }

  function gatherProductData(btn) {
    const container = btn.closest('[data-wishlist-product]') || btn.closest('form') || document;
    const productId = parseInt(btn.dataset.productId);
    const title = btn.dataset.productTitle || container.querySelector('.product__title, h1')?.textContent?.trim() || '';
    const image = btn.dataset.productImage || container.querySelector('.product__media img')?.src || '';
    const url = btn.dataset.productUrl || window.location.pathname;
    const handle = btn.dataset.productHandle || '';
    const price = parseInt(btn.dataset.productPrice || '0');

    // Get variants from data attribute (JSON encoded in liquid)
    let variants = [];
    let options = [];
    try {
      const variantsRaw = btn.dataset.productVariants;
      if (variantsRaw) variants = JSON.parse(variantsRaw);
      const optionsRaw = btn.dataset.productOptions;
      if (optionsRaw) options = JSON.parse(optionsRaw);
    } catch (e) {
      console.error('Wishlist: Failed to parse product variants', e);
    }

    const tags = (btn.dataset.productTags || '').split(',').map(t => t.trim()).filter(Boolean);

    return { productId, title, image, url, handle, price, variants, options, tags };
  }

  /* ─── Collection Page Buttons ─── */
  function initCollectionButtons() {
    // Use capture phase on document to intercept clicks BEFORE Dawn's
    // card link ::after overlay can navigate to the product page
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle-collection]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const productId = parseInt(btn.dataset.productId);

      if (isInWishlist(productId)) {
        removeFromWishlist(productId);
      } else {
        const productData = {
          productId,
          title: btn.dataset.productTitle || '',
          image: btn.dataset.productImage || '',
          url: btn.dataset.productUrl || '',
          handle: btn.dataset.productHandle || '',
          price: parseInt(btn.dataset.productPrice || '0'),
          variants: JSON.parse(btn.dataset.productVariants || '[]'),
          options: JSON.parse(btn.dataset.productOptions || '[]'),
          tags: (btn.dataset.productTags || '').split(',').map(t => t.trim()).filter(Boolean),
        };
        addToWishlist(productData);
        showWishlistNotification(productData.title, productData.image);
        btn.classList.add('wishlist-btn--bounce');
        setTimeout(() => btn.classList.remove('wishlist-btn--bounce'), 600);
      }
    }, true); // true = capture phase
  }

  /* ─── Header Icon ─── */
  function initHeaderIcon() {
    const headerBtn = document.querySelector('.wishlist-header__btn');
    if (headerBtn) {
      headerBtn.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    }
  }

  /* ─── Drawer Event Bindings ─── */
  function initDrawer() {
    const closeBtn = document.querySelector('.wishlist-drawer__close');
    const overlay = document.querySelector('.wishlist-overlay');
    const addAllBtn = document.querySelector('.wishlist-drawer__add-all');
    const viewCartBtn = document.querySelector('.wishlist-drawer__view-cart');

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    if (addAllBtn) {
      addAllBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        addAllBtn.disabled = true;
        addAllBtn.textContent = 'Adding...';
        await addAllToCart();
        addAllBtn.disabled = false;
        addAllBtn.textContent = 'Add all to basket';
      });
    }

    if (viewCartBtn) {
      viewCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
        setTimeout(() => openCartDrawer(), ANIMATION_DURATION);
      });
    }

    // Keyboard escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawerOpen) closeDrawer();
    });
  }

  /* ─── Init ─── */
  async function init() {
    wishlistItems = getWishlist();
    loadVariantSelections();
    await fetchCart();

    initProductPageButton();
    initCollectionButtons();
    initHeaderIcon();
    initDrawer();
    updateAllUI();

    // Listen for Shopify cart updates from other sources
    document.addEventListener('cart:updated', async () => {
      await fetchCart();
      updateAllUI();
    });

    // Also listen for section rendering (Dawn AJAX cart)
    const observer = new MutationObserver(() => {
      fetchCart().then(() => {
        if (drawerOpen) {
          const selections = captureDrawerSelections();
          renderDrawerItems(selections);
        }
        updateDrawerFooter();
      });
    });
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for external use
  window.PupClubWishlist = {
    add: addToWishlist,
    remove: removeFromWishlist,
    isInWishlist,
    getAll: () => [...wishlistItems],
    openDrawer,
    closeDrawer,
  };
})();